import Resizer from 'react-image-file-resizer';

import { getPresignedUrl, uploadImage, finalizeUpload } from 'api/image';

const resizeImageFile = (file, size) =>
  new Promise((resolve) => {
    Resizer.imageFileResizer(
      file,
      size,
      size,
      'JPEG',
      100,
      0,
      (uri) => {
        resolve(uri);
      },
      'base64',
      size,
      size,
    );
  });

const dataURItoBlob = (dataURI) => {
  // eslint-disable-next-line no-undef
  const binary = atob(dataURI.split(',')[1]);
  const array = [];
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < binary.length; i++) {
    array.push(binary.charCodeAt(i));
  }
  return new Blob([new Uint8Array(array)], { type: 'image/jpeg' });
};

const imageUploadHandler = async (
  imageData,
  header,
  uploadFilename,
  bucketPath,
  setUploadStatus,
  startCallback,
  progressCallback,
) => {
  const handleUploadProgress = (evt) => {
    const { loaded, total } = evt;
    if (total <= 0) progressCallback(0);
    progressCallback(loaded, total);
  };

  const response = await getPresignedUrl({
    filename: uploadFilename,
    path: bucketPath,
    header,
  });

  const { data, ok } = response;

  if (!ok) {
    const { errors } = data;
    if (errors) {
      const { header: errorHeader } = errors;
      const errMessage = errorHeader.length > 0 ? errorHeader[0] : 'Could not upload this file. Please check the file.';
      throw new Error(errMessage);
    }
    throw new Error('Something went wrong. Please try again.');
  }

  startCallback(uploadFilename);

  let dataUrl;
  try {
    const { action, fields } = data;
    const { value: mimeType } = fields.find(field => field.name === 'Content-Type');
    if (!mimeType) {
      throw new Error('File type was not determined.');
    }
    if (mimeType !== 'image/jpeg' && mimeType !== 'image/png' && mimeType !== 'image/gif') {
      throw new Error('File type not allowed. Must be .jpeg, .png, or .gif.');
    }

    const params = { onUploadProgress: handleUploadProgress };

    const uploadResponse = await uploadImage(action, fields, dataURItoBlob(imageData), params);
    const { key } = uploadResponse;
    if (key) {
      const finalizeResponse = await finalizeUpload({ key });
      const { data: url } = finalizeResponse;
      dataUrl = url;
    }
  } catch (e) {
    throw new Error(e.message);
  }

  if (!dataUrl) return;

  setUploadStatus('success');
  return dataUrl;
};

export const uploadImageToS3 = async (
  newImage,
  header,
  setUploadStatus,
  startCallback,
  progressCallback,
) => {
  let thumbnailUrl = null;
  const imageData = newImage.data_url;
  const filename = newImage?.file.name;
  if (!imageData || !filename) {
    const message = !imageData ?
      'There were no contents in the file.' :
      'File did not have a name.';
    setUploadStatus('failed', message);
    progressCallback(0, 100);
    return null;
  }

  // Uploading the thumbnail image
  try {
    const thumbnail = await resizeImageFile(newImage.file, 100);
    thumbnailUrl = await imageUploadHandler(
      thumbnail,
      header,
      filename,
      'submission_thumbnail',
      setUploadStatus,
      startCallback,
      progressCallback,
    );
    if (!thumbnailUrl) return null;
  } catch (err) {
    // console.log(err);
    return null;
  }

  // Uploading the preview image
  try {
    const preview = await resizeImageFile(newImage.file, 430);
    const previewUrl = await imageUploadHandler(
      preview,
      header,
      filename,
      'submission_preview',
      setUploadStatus,
      startCallback,
      progressCallback,
    );
    if (!previewUrl) return null;
  } catch (err) {
    // console.log(err);
    return null;
  }

  // Uploading the original image
  try {
    const originalUrl = await imageUploadHandler(
      imageData,
      header,
      filename,
      's3fs-public',
      setUploadStatus,
      startCallback,
      progressCallback,
    );
    if (!originalUrl) return null;
    return thumbnailUrl;
  } catch (err) {
    // console.log(err);
    return null;
  }
};


const editorImageUploadHandler = async (
  imageData,
  header,
  uploadFilename,
  bucketPath,
  setUploadStatus,
  startCallback,
  progressCallback,
) => {
  if (!bucketPath) {
    const message = 'No upload path was provided.';
    setUploadStatus('failed', message);
    progressCallback(0, 100);
    return;
  }

  const handleUploadProgress = (evt) => {
    const { loaded, total } = evt;
    if (total <= 0)  progressCallback(0);
    progressCallback(loaded / total);
  };

  const response = await getPresignedUrl({
    filename: uploadFilename,
    path: bucketPath,
    header,
  });

  const { data, ok } = response;

  if (!ok) {
    const { errors } = data;
    if (errors) {
      const { header: errorHeader } = errors;
      const errMessage = errorHeader.length > 0 ? errorHeader[0] : 'Could not upload this file. Please check the file.';
      throw new Error(errMessage);
    }
    throw new Error('Something went wrong. Please try again.');
  }

  startCallback(uploadFilename);

  let dataUrl;
  try {
    const { action, fields } = data;
    const { value: mimeType } = fields.find(field => field.name === 'Content-Type');
    if (!mimeType) {
      throw new Error('File type was not determined.');
    }
    if (
      mimeType !== 'image/jpeg' &&
      mimeType !== 'image/png' &&
      mimeType !== 'image/gif' &&
      mimeType !== 'image/webp')
    {
      throw new Error('File type not allowed. Must be .jpeg, .png, .gif or .webp');
    }

    const params = { onUploadProgress: handleUploadProgress };

    const uploadResponse = await uploadImage(action, fields, dataURItoBlob(imageData), params);
    const { key } = uploadResponse;
    if (key) {
      const finalizeResponse = await finalizeUpload({ key });
      const { data: url } = finalizeResponse;
      dataUrl = url;
    }
  } catch (e) {
    throw new Error(e.message);
  }

  if (!dataUrl) return;

  setUploadStatus('success');
  return dataUrl;
};

export const uploadEditorImageToS3 = async (
  newImage,
  header,
  path,
  setUploadStatus,
  startCallback,
  progressCallback,
) => {
  const imageData = newImage.url;
  const originalFilename = newImage?.name;

  // Need to lowercase a filename extension to prevent sanitizer from cleaning it.
  const filenameSplit = originalFilename.split('.');
  const splitLength = filenameSplit.length ?? 0;

  if (splitLength < 2) {
    const message = 'The file did not have a valid file name.';
    setUploadStatus('failed', message);
    progressCallback(0, 100);
    return { error: message };
  }
  filenameSplit[splitLength - 1] = filenameSplit[splitLength - 1].toLowerCase();
  const filename = filenameSplit.reduce((_filename, word, index) => {
    if (index === splitLength - 1) return `${_filename}.${word}`;
    return `${_filename}${word}`;
  });

  if (!imageData || !filename) {
    const message = !imageData ?
     'There were no contents in the file.' :
     'File did not have a name.';
    setUploadStatus('failed', message);
    progressCallback(0, 100);
    return { error: message };
  }

  // Upload the original image
  try {
    const originalUrl = await editorImageUploadHandler(
      imageData,
      header,
      filename,
      path,
      setUploadStatus,
      startCallback,
      progressCallback,
    );

    if (!originalUrl) {
      const message = 'Could not get a url for the uploaded file.';
      setUploadStatus('failed', message);
      progressCallback(0);
      return { error: message };
    }
    // If filename has + in it, they need to be replaced with the encoded string
    // and encodeURI() doesn't handle + and cloudfront urls will convert + in filename to %2B for the url.
    return encodeURI(originalUrl).replaceAll('+', '%2B');
  } catch (err) {
    setUploadStatus('failed', err.message);
    progressCallback(0);
    throw new Error(err.message);
  }
};
